import { z } from 'zod';
/**
 * Найм персонала.
 *
 * Воронка: отклик → звонок → запись на экскурсию → явка → запись на стажировку
 * → явка → результат стажировки → трудоустройство.
 *
 * Ключевая метрика — процент неявок: люди записываются и не приходят,
 * и именно это съедает время HR.
 */
export const CANDIDATE_STATUS_NAMES = {
    APPLIED: 'Отклик',
    CALL_NO_ANSWER: 'Недозвон',
    CALL_DONE: 'Дозвонились',
    TOUR_SCHEDULED: 'Записан на экскурсию',
    TOUR_SHOWED: 'Был на экскурсии',
    TOUR_NO_SHOW: 'Не пришёл на экскурсию',
    INTERNSHIP_SCHEDULED: 'Записан на стажировку',
    INTERNSHIP_SHOWED: 'Был на стажировке',
    INTERNSHIP_NO_SHOW: 'Не пришёл на стажировку',
    INTERNSHIP_PASSED: 'Прошёл стажировку',
    INTERNSHIP_FAILED: 'Не прошёл стажировку',
    LEFT: 'Ушёл сам',
    HIRED: 'Трудоустроен',
    NOT_SUITABLE: 'Не подходит',
    DECLINED: 'Отказался',
};
/** Этапы воронки в том порядке, в котором считается конверсия. */
export const FUNNEL_STEPS = [
    { key: 'applied', title: 'Отклики' },
    { key: 'called', title: 'Дозвонились' },
    { key: 'tourScheduled', title: 'Записаны на экскурсию' },
    { key: 'tourShowed', title: 'Пришли на экскурсию' },
    { key: 'internshipScheduled', title: 'Записаны на стажировку' },
    { key: 'internshipShowed', title: 'Пришли на стажировку' },
    { key: 'passed', title: 'Прошли стажировку' },
    { key: 'hired', title: 'Трудоустроены' },
];
/** Статусы, из которых человек уже выбыл из воронки. */
export const CLOSED_STATUSES = [
    'TOUR_NO_SHOW',
    'INTERNSHIP_NO_SHOW',
    'INTERNSHIP_FAILED',
    'LEFT',
    'NOT_SUITABLE',
    'DECLINED',
    'HIRED',
];
export function isClosed(status) {
    return CLOSED_STATUSES.includes(status);
}
// ───────────────────────────── телефон ─────────────────────────────
/**
 * Приведение телефона к виду +7XXXXXXXXXX.
 *
 * Это ключ поиска дублей и неявок: один и тот же человек звонит с одного номера,
 * но записывает его каждый раз по-разному. Без нормализации реестр неявок
 * бесполезен — совпадений просто не будет.
 */
export function normalizePhone(input) {
    const digits = input.replace(/\D/g, '');
    if (digits.length === 0)
        return null;
    let national = digits;
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
        national = digits.slice(1);
    }
    else if (digits.length === 10) {
        national = digits;
    }
    else {
        return null;
    }
    if (national.length !== 10)
        return null;
    return `+7${national}`;
}
/** «+79161234567» → «+7 916 123-45-67». */
export function formatPhone(normalized) {
    const digits = normalized.replace(/\D/g, '');
    if (digits.length !== 11)
        return normalized;
    const rest = digits.slice(1);
    return `+7 ${rest.slice(0, 3)} ${rest.slice(3, 6)}-${rest.slice(6, 8)}-${rest.slice(8, 10)}`;
}
// ───────────────────────────── события ─────────────────────────────
export const candidateCreateSchema = z.object({
    candidateId: z.string().uuid(),
    fullName: z.string().trim().min(3).max(200),
    phone: z.string().trim().min(5).max(30),
    source: z.string().trim().max(100).nullish(),
    appliedAt: z.string().datetime({ offset: true }),
    comment: z.string().trim().max(2000).nullish(),
    /**
     * HR подтвердил, что видел предупреждение о повторном телефоне.
     * Автоматического отказа нет: решение принимает человек.
     */
    duplicateAcknowledged: z.boolean().default(false),
});
export const candidateCallSchema = z.object({
    candidateId: z.string().uuid(),
    outcome: z.enum(['SCHEDULED', 'NO_ANSWER', 'DECLINED', 'NOT_SUITABLE']),
    comment: z.string().trim().max(1000).nullish(),
});
export const appointmentScheduleSchema = z.object({
    appointmentId: z.string().uuid(),
    candidateId: z.string().uuid(),
    kind: z.enum(['TOUR', 'INTERNSHIP']),
    /** Время начала: слоты 10:00, 11:00, 13:00, 14:00. */
    scheduledAt: z.string().datetime({ offset: true }),
    siteId: z.string().uuid().nullish(),
});
export const attendanceSchema = z.object({
    appointmentId: z.string().uuid(),
    candidateId: z.string().uuid(),
    showed: z.boolean(),
});
export const internshipResultSchema = z.object({
    appointmentId: z.string().uuid(),
    candidateId: z.string().uuid(),
    result: z.enum(['PASSED', 'FAILED', 'LEFT', 'NO_SHOW']),
    reason: z.string().trim().max(1000).nullish(),
});
export const candidateHireSchema = z.object({
    candidateId: z.string().uuid(),
    /** Вход нового сотрудника задаёт администратор при оформлении. */
    login: z
        .string()
        .trim()
        .toLowerCase()
        .min(2)
        .max(64)
        .regex(/^[a-z0-9._-]+$/, 'Логин — латинские буквы, цифры, точка, дефис'),
    password: z.string().min(8, 'Пароль короче восьми символов').max(200),
    /** Дата выхода на работу: может быть в прошлом, от неё считается выслуга. */
    hiredAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД'),
    siteIds: z.array(z.string().uuid()).min(1),
});
/**
 * Запись на собеседование одним действием.
 *
 * HR звонит по откликам и тут же назначает время: отдельно заводить карточку,
 * а потом искать её в списке, чтобы записать на слот, — это два лишних шага
 * во время разговора. Если человек по этому телефону уже есть, карточка
 * переиспользуется, вторая не создаётся.
 */
export const quickAppointmentSchema = z.object({
    fullName: z.string().trim().min(3, 'Укажите имя и фамилию').max(120),
    phone: z.string().trim().min(5, 'Укажите телефон').max(32),
    kind: z.enum(['TOUR', 'INTERNSHIP']),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД'),
    /** Время слота в местном времени производства, «ЧЧ:ММ». */
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время в формате ЧЧ:ММ'),
});
/** Настройка слотов: во сколько зовут и сколько человек берут на одно время. */
export const interviewSlotsSchema = z.object({
    slots: z
        .array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время в формате ЧЧ:ММ'))
        .min(1, 'Оставьте хотя бы одно время')
        .max(12, 'Слишком много слотов'),
    capacity: z.number().int().min(1).max(50),
});
export const candidateCommentSchema = z.object({
    candidateId: z.string().uuid(),
    comment: z.string().trim().min(1).max(2000),
});
export const candidateEraseSchema = z.object({
    candidateId: z.string().uuid(),
    reason: z.string().trim().min(3).max(500),
});
export function conversions(counts) {
    const result = [];
    for (let index = 1; index < FUNNEL_STEPS.length; index += 1) {
        const from = FUNNEL_STEPS[index - 1];
        const to = FUNNEL_STEPS[index];
        const fromCount = counts[from.key];
        const toCount = counts[to.key];
        result.push({
            from: from.key,
            to: to.key,
            title: `${from.title} → ${to.title}`,
            fromCount,
            toCount,
            rateBp: fromCount === 0 ? 0 : Math.round((toCount * 10_000) / fromCount),
        });
    }
    return result;
}
/**
 * Процент неявок отдельно по экскурсиям и стажировкам.
 * Скорее всего, именно эта пара цифр и болит.
 */
export function noShowRateBp(scheduled, showed) {
    if (scheduled === 0)
        return 0;
    return Math.round(((scheduled - showed) * 10_000) / scheduled);
}
export const CANDIDATE_RETENTION_DAYS = 180;
//# sourceMappingURL=hiring.js.map